import { useCallback, useEffect, useRef, useState } from 'react'

/** Cycled playback rates, in tap order, starting at 1×. */
const SPEEDS = [1, 1.5, 2, 0.5, 0.75] as const

/**
 * Audio playback state for a single recording: play/pause, smooth progress,
 * scrub-to-seek, and a cycling speed control. The `<audio>` element is owned by
 * the component and wired through the returned `audioRef`.
 */
export function useRecordingPlayer(durationSeconds: number) {
  const audioRef = useRef<HTMLAudioElement>(null)
  const [playing, setPlaying] = useState(false)
  const [current, setCurrent] = useState(0)
  const [total, setTotal] = useState(durationSeconds)
  const [speedIndex, setSpeedIndex] = useState(0)
  const speed = SPEEDS[speedIndex]

  // Drive the progress off the animation frame while playing — `timeupdate`
  // only fires ~4×/sec, which makes the bar visibly step; rAF fills it smoothly.
  useEffect(() => {
    if (!playing) return
    let frame = 0
    const tick = () => {
      const audio = audioRef.current
      if (audio) setCurrent(audio.currentTime)
      frame = requestAnimationFrame(tick)
    }
    frame = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(frame)
  }, [playing])

  // Keep the element's rate in sync with the cycled speed (also re-applied when
  // a new src loads, since the browser resets playbackRate to 1 on load).
  useEffect(() => {
    const audio = audioRef.current
    if (audio) audio.playbackRate = speed
  }, [speed])

  const toggle = useCallback(() => {
    const audio = audioRef.current
    if (!audio) return
    if (audio.paused) void audio.play()
    else audio.pause()
  }, [])

  /** Jump to a fraction (0–1) of the total length. */
  const seekToFraction = useCallback(
    (fraction: number) => {
      const audio = audioRef.current
      if (!audio || total <= 0) return
      const time = Math.min(1, Math.max(0, fraction)) * total
      audio.currentTime = time
      setCurrent(time)
    },
    [total],
  )

  const cycleSpeed = useCallback(
    () => setSpeedIndex((index) => (index + 1) % SPEEDS.length),
    [],
  )

  const progress = total > 0 ? Math.min(100, (current / total) * 100) : 0

  return {
    audioRef,
    playing,
    setPlaying,
    current,
    total,
    setTotal,
    setCurrent,
    progress,
    speed,
    toggle,
    seekToFraction,
    cycleSpeed,
  }
}
